
import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import PageHero from '@/components/ui/PageHero';
import ScrollReveal from '@/components/ui/ScrollReveal';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Camera, Video, Home, Image, Map, Box, LayoutGrid, Award } from 'lucide-react';
import PricingSection from '@/components/ui/PricingSection';
import PricingCard from '@/components/ui/PricingCard';
import FAQSection from '@/components/ui/FAQSection';
import { Card, CardContent } from '@/components/ui/card';

const RealEstateMedia = () => {
  const pricingCards = [
    {
      title: "Standard MLS Photography",
      price: "$215",
      description: "Property photos for listings in Baton Rouge, Louisiana. 25 photos included.",
      features: [
        { text: "High-quality professional photos", included: true },
        { text: "Photo selection gallery", included: true },
        { text: "Delivery within 3-5 business days", included: true },
        { text: "Digital delivery", included: true },
        { text: "Perfect for MLS listings", included: true },
      ],
      popular: false,
      ctaText: "Book Now",
      ctaLink: "/contact"
    },
    {
      title: "Premium MLS Photography",
      price: "$255",
      description: "Enhanced property photos for Baton Rouge, Louisiana listings. 40 photos included.",
      features: [
        { text: "High-quality professional photos", included: true },
        { text: "Photo selection gallery", included: true },
        { text: "Delivery within 3-5 business days", included: true },
        { text: "Digital delivery", included: true },
        { text: "Perfect for larger homes", included: true },
      ],
      popular: true,
      ctaText: "Book Now",
      ctaLink: "/contact"
    },
    {
      title: "Architectural Photography",
      price: "$600",
      description: "Magazine-style photos that showcase architectural details in Baton Rouge, Louisiana properties.",
      features: [
        { text: "20 premium magazine-style photos", included: true },
        { text: "Advanced photographic techniques", included: true },
        { text: "Specialized lens applications", included: true },
        { text: "Perfect for luxury properties", included: true },
        { text: "Ideal for marketing materials", included: true },
      ],
      badges: [
        { text: "Premium Photography" },
        { text: "Luxury Captures" },
        { text: "Cinematic Stills" }
      ],
      popular: false,
      ctaText: "Book Now",
      ctaLink: "/contact"
    }
  ];

  const tourPricingCards = [
    {
      title: "360° Virtual Tours for ShowingTime",
      price: "From $150",
      description: "Immersive virtual tours for Baton Rouge properties integrated with Zillow ShowingTime platform.",
      features: [
        { text: "1,500 sq ft: $150", included: true },
        { text: "1,500-3,000 sq ft: $225", included: true },
        { text: "3,000-4,000 sq ft: $350", included: true },
        { text: "High-resolution 360° camera technology", included: true },
        { text: "Direct upload to Zillow platform", included: true },
      ],
      badges: [
        { text: "3D Video" },
        { text: "3D Walkthrough" },
        { text: "Matterport Alternative" }
      ],
      popular: false,
      ctaText: "Book Now",
      ctaLink: "/contact"
    },
    {
      title: "Custom 360° Virtual Tours",
      price: "From $300",
      description: "Custom virtual tours for websites and landing pages for Baton Rouge, Louisiana properties.",
      features: [
        { text: "15 Panos: $300", included: true },
        { text: "40 Panos: $800", included: true },
        { text: "100 Panos: $2,000", included: true },
        { text: "Website integration", included: true },
        { text: "Great for apartments & rentals", included: true },
      ],
      badges: [
        { text: "3D Video" },
        { text: "Virtual Tour for your website" },
        { text: "Matterport Alternative" }
      ],
      popular: true,
      ctaText: "Book Now",
      ctaLink: "/contact"
    }
  ];

  const videoPricingCards = [
    {
      title: "Real Estate Property Videos",
      price: "From $375",
      description: "Professional video capture and editing for Baton Rouge, Louisiana property marketing.",
      features: [
        { text: "1-minute video: $375", included: true },
        { text: "2-minute video: $500", included: true },
        { text: "Royalty-free music", included: true },
        { text: "Social media optimized", included: true },
        { text: "Drone footage available ($100-$250)", included: true },
      ],
      badges: [
        { text: "Virtual Tours and Walkthroughs" },
        { text: "Cinematic Optional" },
        { text: "Talking Head Optional" }
      ],
      popular: false,
      ctaText: "Book Now",
      ctaLink: "/contact"
    },
    {
      title: "Contractor Drone Highlight Videos",
      price: "$285",
      description: "Aerial drone video capture for contractors and construction projects in Baton Rouge, Louisiana.",
      features: [
        { text: "1-minute video with music", included: true },
        { text: "Logo integration", included: true },
        { text: "3 high-resolution drone photos", included: true },
        { text: "Social media optimized", included: true },
        { text: "Vertical or horizontal format", included: true },
      ],
      badges: [
        { text: "Construction" },
        { text: "Contractors" },
        { text: "Social Media Content" }
      ],
      popular: false,
      ctaText: "Book Now",
      ctaLink: "/contact"
    }
  ];

  const faqs = [
    {
      question: "How quickly can you schedule a photo shoot in Baton Rouge?",
      answer: "We typically schedule shoots within 48-72 hours of booking, depending on current demand in the Baton Rouge area. For urgent requests, we offer expedited scheduling for an additional fee, subject to availability."
    },
    {
      question: "How long does it take to receive the final images?",
      answer: "Our standard delivery time is 3-5 business days after your photo selection. After we capture the images, we send a gallery for selection then you select the amount of photos you purchased in the package. We will then start the editing process and deliver digitally when ready."
    },
    {
      question: "What if the weather is bad on the day of the shoot?",
      answer: "If weather conditions in Baton Rouge aren't ideal for exterior photography, we can either focus on interior shots and return another day for exteriors, or reschedule the entire shoot at no additional cost."
    },
    {
      question: "Do you offer twilight photography?",
      answer: "Yes, we offer virtual twilight photography as an add-on service to any package. These virtual twilight edits create a dramatic and emotional appeal that can help your Baton Rouge listing stand out."
    },
    {
      question: "Are there additional fees I should be aware of?",
      answer: "Additional fees might include travel fees (for properties outside Baton Rouge), expedited turnaround, special edits, additional pictures beyond your package, and organization/cleanup fees if spaces are not properly organized before photography arrival."
    }
  ];

  return (
    <PageLayout 
      title="Real Estate Media | Astrem" 
      description="Specialized media services for real estate professionals including photography, virtual tours, and promotional content."
      ogImage="public/lovable-uploads/0ae37432-fcfd-489e-9876-96dbf3fd5d58.png"
    >
      <PageHero
        title="Real Estate Media Services"
        subtitle="Showcase Baton Rouge Properties at Their Absolute Best"
        description="Professional photography, videography, virtual tours, and media services designed specifically for Baton Rouge real estate professionals to help properties sell faster and for higher prices."
        badge="Real Estate Media"
        backgroundImage="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/luxury-property-photography.jpg"
        ctaText="Book a Shoot"
        ctaLink="/contact"
      />

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
              Our Services
            </span>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
              Comprehensive Real Estate Media Solutions in Baton Rouge
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={200}>
            <p className="text-lg text-astrem-gray-600 max-w-3xl mb-12">
              We provide a full range of professional media services designed to showcase Baton Rouge properties at their best and help real estate professionals stand out in a competitive market.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <ScrollReveal delay={300}>
              <div className="p-6 border border-astrem-gray-200 rounded-lg hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-astrem-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Camera className="h-6 w-6 text-astrem-black" />
                </div>
                <h3 className="text-xl font-medium mb-3">Professional Photography</h3>
                <p className="text-astrem-gray-600">
                  High-quality, professionally edited property photos that highlight the best features of any Baton Rouge space using advanced equipment and lighting techniques.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={400}>
              <div className="p-6 border border-astrem-gray-200 rounded-lg hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-astrem-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Video className="h-6 w-6 text-astrem-black" />
                </div>
                <h3 className="text-xl font-medium mb-3">Cinematic Video Tours</h3>
                <p className="text-astrem-gray-600">
                  Professionally produced video walkthroughs that tell the story of a Baton Rouge property and create an emotional connection with potential buyers.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={500}>
              <div className="p-6 border border-astrem-gray-200 rounded-lg hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-astrem-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Map className="h-6 w-6 text-astrem-black" />
                </div>
                <h3 className="text-xl font-medium mb-3">Aerial Drone Photography</h3>
                <p className="text-astrem-gray-600">
                  Stunning aerial photos and videos that showcase the property, its surroundings, and highlight features like large lots, views, and proximity to Baton Rouge amenities.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={600}>
              <div className="p-6 border border-astrem-gray-200 rounded-lg hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-astrem-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Box className="h-6 w-6 text-astrem-black" />
                </div>
                <h3 className="text-xl font-medium mb-3">3D Virtual Tours</h3>
                <p className="text-astrem-gray-600">
                  Immersive 3D virtual tours that allow potential buyers to explore Baton Rouge properties remotely at their own pace, reducing unnecessary showings.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={700}>
              <div className="p-6 border border-astrem-gray-200 rounded-lg hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-astrem-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Home className="h-6 w-6 text-astrem-black" />
                </div>
                <h3 className="text-xl font-medium mb-3">Floor Plans</h3>
                <p className="text-astrem-gray-600">
                  Accurate 2D and 3D floor plans that help buyers understand the layout and flow of a Baton Rouge property before visiting in person.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={800}>
              <div className="p-6 border border-astrem-gray-200 rounded-lg hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-astrem-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Image className="h-6 w-6 text-astrem-black" />
                </div>
                <h3 className="text-xl font-medium mb-3">Virtual Staging</h3>
                <p className="text-astrem-gray-600">
                  Digital staging services that show the potential of empty spaces in Baton Rouge homes without the cost of physical staging, helping buyers envision themselves in the property.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <img
                src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/luxury-property-photography.jpg"
                alt="Real Estate Photography in Baton Rouge"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  Why Choose Us
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Benefits of Professional Real Estate Media in Baton Rouge
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-8">
                  High-quality property media is no longer optional in today's competitive Baton Rouge real estate market. See how our services can make a difference:
                </p>
                
                <div className="space-y-6">
                  <div className="flex">
                    <div className="flex-shrink-0 mr-4">
                      <Award className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium mb-2">Higher Selling Prices</h3>
                      <p className="text-astrem-gray-600">
                        Properties with professional photography sell for up to 3-5% more than listings with amateur photos in the Baton Rouge market.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex">
                    <div className="flex-shrink-0 mr-4">
                      <LayoutGrid className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium mb-2">More Online Interest</h3>
                      <p className="text-astrem-gray-600">
                        Listings with professional photos receive 61% more online views, significantly expanding your buyer pool in Baton Rouge.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex">
                    <div className="flex-shrink-0 mr-4">
                      <Home className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium mb-2">Faster Sales</h3>
                      <p className="text-astrem-gray-600">
                        Baton Rouge properties with high-quality visuals sell 32% faster than those with standard or poor-quality images.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex">
                    <div className="flex-shrink-0 mr-4">
                      <Box className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium mb-2">Reduced Showings</h3>
                      <p className="text-astrem-gray-600">
                        Virtual tours and detailed visuals help pre-qualify buyers, reducing unnecessary showings to only serious prospects interested in Baton Rouge properties.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Portfolio Showcase */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Our Work
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                Showcasing Baton Rouge Properties at Their Best
              </h2>
              <p className="text-lg text-astrem-gray-600">
                Browse through some examples of our professional real estate photography and media services in Baton Rouge, Louisiana.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <ScrollReveal delay={200}>
              <div className="overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <img
                  src="https://media.milanote.com/p/images/1P2Vke1lxhWe3R/QgY/Cagley+Family+Home+-+Rouzan+%282+of+35%29.jpg"
                  alt="New Construction in Baton Rouge"
                  className="w-full h-64 object-cover"
                />
                <div className="p-4">
                  <h3 className="text-lg font-medium mb-1">New Construction</h3>
                  <p className="text-sm text-astrem-gray-600">Premium Photography Session</p>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={300}>
              <div className="overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <img
                  src="https://media.milanote.com/p/images/1P2Vke1lxhWe3W/4eX/Cagley+Homes+-+Astrem+-+03412.jpg"
                  alt="New Construction in Baton Rouge"
                  className="w-full h-64 object-cover"
                />
                <div className="p-4">
                  <h3 className="text-lg font-medium mb-1">New Construction</h3>
                  <p className="text-sm text-astrem-gray-600">Premium Photography Session</p>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={400}>
              <div className="overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <img
                  src="https://media.milanote.com/p/images/1OXGwL1PCHmxbQ/7Bp/Antonio+Lopez+-+11012+Crossover+Rd+%2823+of+24%29.jpg"
                  alt="Remodeled Home in Baton Rouge"
                  className="w-full h-64 object-cover"
                />
                <div className="p-4">
                  <h3 className="text-lg font-medium mb-1">Remodeled Home</h3>
                  <p className="text-sm text-astrem-gray-600">Virtual Twilight Property Photo</p>
                </div>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={500} className="mt-10 text-center">
            <Button asChild className="bg-astrem-black hover:bg-astrem-gray-800 text-white rounded-full px-6">
              <Link to="/contact">View More Examples</Link>
            </Button>
          </ScrollReveal>
        </div>
      </section>

      {/* Pricing Sections */}
      <PricingSection
        title="Photography Services in Baton Rouge"
        description="Professional photography packages for Baton Rouge, Louisiana real estate properties."
        pricingCards={pricingCards}
      />

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Virtual Tour Options
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                3D Virtual Tours for Baton Rouge Properties
              </h2>
              <p className="text-lg text-astrem-gray-600">
                Give potential buyers an immersive experience of your Baton Rouge property with our virtual tour options.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {tourPricingCards.map((card, index) => (
              <ScrollReveal key={index} delay={200 + (index * 100)}>
                <PricingCard {...card} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Video Services
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                Property Videos for Baton Rouge Real Estate
              </h2>
              <p className="text-lg text-astrem-gray-600">
                Showcase your Baton Rouge property with professional video content that engages potential buyers.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {videoPricingCards.map((card, index) => (
              <ScrollReveal key={index} delay={200 + (index * 100)}>
                <PricingCard {...card} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Client Feedback
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                What Baton Rouge Real Estate Professionals Say
              </h2>
              <p className="text-lg text-astrem-gray-600">
                Hear from agents and brokers who have transformed their Baton Rouge listings with our professional media services.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ScrollReveal delay={200}>
              <div className="bg-white p-6 rounded-lg border border-astrem-gray-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div>
                    <h4 className="font-medium">Simon Shuler</h4>
                    <p className="text-sm text-astrem-gray-600">Luxury Real Estate Agent</p>
                  </div>
                </div>
                <p className="text-astrem-gray-600 italic">
                  "Evan is very patient, and delivers exceptional quality service and media. I was thoroughly impressed with my listing photos, cinematic property tour, landing pages, and 3D walkthrough… Highly recommend."
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={300}>
              <div className="bg-white p-6 rounded-lg border border-astrem-gray-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div>
                    <h4 className="font-medium">Kaylee Brock</h4>
                    <p className="text-sm text-astrem-gray-600">Marketing Manager</p>
                  </div>
                </div>
                <p className="text-astrem-gray-600 italic">
                  "Evan helped us shoot a lifestyle video for an apartment complex in Baton Rouge. He was very accommodating with any changes we needed to make and very responsive. Overall, we had a great experience, and the video turned out great!"
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={400}>
              <div className="bg-white p-6 rounded-lg border border-astrem-gray-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div>
                    <h4 className="font-medium">Nick Cagley</h4>
                    <p className="text-sm text-astrem-gray-600">Cagley Homes - Luxury Homes</p>
                  </div>
                </div>
                <p className="text-astrem-gray-600 italic">
                  "Evan has been photographing and filming my projects the last few years. He is very professional and does a very good job of getting all the details we want for our projects. I look forward to photographing our next project with him!"
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection
        title="Frequently Asked Questions"
        description="Find answers to common questions about our real estate media services in Baton Rouge, Louisiana."
        faqs={faqs}
      />

      {/* CTA Section */}
      <section className="py-20 bg-astrem-black text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollReveal>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                Ready to Showcase Your Baton Rouge Property at Its Best?
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <p className="text-lg text-astrem-gray-300 mb-8">
                Contact us today to book a shoot or learn more about our real estate media services in Baton Rouge, Louisiana.
              </p>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <Button asChild size="lg" className="bg-white text-astrem-black hover:bg-astrem-gray-100 rounded-full px-8">
                <Link to="/contact">Contact Us</Link>
              </Button>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default RealEstateMedia;
